import React from 'react';
import { toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as Success } from 'icons/success.svg';
import { ReactComponent as Warning } from 'icons/warning.svg';
import {
  STATEMENT_CONTENT_PANEL_ZOOM_BLOCK,
  STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK,
} from './statement-content-constants';

export const ANNOTATION_MAP_ACTION_BLOCK = 'annotation-map-action';
const BANNER_ICON_SIZE = '10';

export const getExcessiveElementSelectionNotification = (elementLimit) => {
  return toast.error(
    <div>
      <Warning
        name={'warning'}
        width={BANNER_ICON_SIZE}
        height={BANNER_ICON_SIZE}
      />
      <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__notify-header`}>
        <strong>
          <FormattedMessage
            values={{
              numberOfElements: elementLimit,
            }}
            id="element-panel.annotation.content.element-map-notify"
          />
        </strong>
      </div>
    </div>,
    { autoClose: 5000 },
  );
};

// this function is used to segregate the source and current(target) elements from array of objects based on elementUpdateStatus property to identify success and failed mapped elements
export const seggregateSourceTargetElements = (elementsMapped) => {
  let successSourceElements = [];
  let failedSourceElements = [];
  let successTargetElements = [];
  let failedTargetElements = [];
  for (let i = 0; i < elementsMapped.length; i++) {
    if (elementsMapped[i].elementUpdateStatus) {
      successSourceElements.push(elementsMapped[i].sourceElementId);
      successTargetElements.push(elementsMapped[i].targetElementId);
    } else {
      failedSourceElements.push(elementsMapped[i].sourceElementId);
      failedTargetElements.push(elementsMapped[i].targetElementId);
    }
  }
  return {
    successSourceElements,
    failedSourceElements,
    successTargetElements,
    failedTargetElements,
  };
};

//successful notification for element(s) mapped
export const successElementMapNotification = ({ targetElements }) => {
  return toast.success(
    <div>
      <Success
        name={'success'}
        width={BANNER_ICON_SIZE}
        height={BANNER_ICON_SIZE}
      />
      <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__success-header`}>
        <strong>
          <FormattedMessage
            id="element-panel.annotation.content.element-map-sucess"
            values={{
              br: (
                <br
                  key={`${ANNOTATION_MAP_ACTION_BLOCK}__success-header-brTag`}
                />
              ),
            }}
          />
        </strong>
      </div>
      <FormattedMessage
        values={{
          numberOfElements: targetElements.length,
          br: (
            <br key={`${ANNOTATION_MAP_ACTION_BLOCK}__success-summary-brTag`} />
          ),
        }}
        id="element-panel.annotation.content.element-map-sucess-summary"
      />
    </div>,
    { autoClose: 5000 },
  );
};

//failure notification for element(s) mapped
export const failureElementMapNotification = ({ targetElements }) => {
  return toast.error(
    <div>
      <Warning
        name={'warning'}
        width={BANNER_ICON_SIZE}
        height={BANNER_ICON_SIZE}
      />
      <div className={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-header`}>
        <strong>
          <FormattedMessage
            id="element-panel.annotation.content.element-map-fail"
            values={{
              br: (
                <br key={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-header-brTag`} />
              ),
            }}
          />
        </strong>
      </div>
      <FormattedMessage
        values={{
          numberOfElements: targetElements.length,
          br: <br key={`${ANNOTATION_MAP_ACTION_BLOCK}__fail-summary-brTag`} />,
        }}
        id="element-panel.annotation.content.element-map-fail-summary"
      />
    </div>,
    { autoClose: 5000 },
  );
};

export const getCalloutPosition = ({
  position,
  calloutRef,
  id,
  zoom,
  leftSideView,
}) => {
  const zoomPercentageSubtractionLR = 10;
  const zoomPercentageSubtractionTB = 8;
  const bodyPadding = 5;
  let calloutPosition = position;

  const calloutBoundingInfo =
    (calloutRef &&
      calloutRef.current &&
      calloutRef.current.getBoundingClientRect()) ||
    {};
  const { height: calloutHeight, width: calloutWidth } = calloutBoundingInfo;

  const div = calloutRef && calloutRef.current;
  const afterStyles = div && window.getComputedStyle(div, '::after');
  const afterContentWidth =
    afterStyles &&
    afterStyles.getPropertyValue('width') &&
    parseInt(afterStyles.getPropertyValue('width').split('p')[0]);
  const afterContentHeight =
    afterStyles &&
    afterStyles.getPropertyValue('height') &&
    parseInt(afterStyles.getPropertyValue('height').split('p')[0]);

  const documentId = leftSideView
    ? STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK
    : STATEMENT_CONTENT_PANEL_ZOOM_BLOCK;
  const parentElement = document && document.getElementById(documentId);

  const {
    left: parentLeftPosition,
    top: parentTopPosition,
    right: parentRightPosition,
  } = parentElement ? parentElement.getBoundingClientRect() : {};

  const tieoutElement = document.getElementById(id);
  const { right, top, height, left, width } = tieoutElement
    ? tieoutElement.getBoundingClientRect()
    : {};
  const elementCenter = height / 2;
  const calloutCenter = calloutHeight / 2;

  let value;
  if (position === 'left')
    value = left - parentLeftPosition - calloutWidth + bodyPadding;
  if (position === 'right')
    value = parentRightPosition - right - calloutWidth + bodyPadding;
  let callout = {};

  if (position === 'left' && value < 0) {
    calloutPosition = 'right';
  }
  if (position === 'right' && value < 0) {
    calloutPosition = 'left';
  }

  const customWidth = (width - calloutWidth) / 2;

  switch (calloutPosition) {
    case 'left':
      callout = {
        left:
          left &&
          afterContentWidth &&
          `${
            (left - parentLeftPosition - calloutWidth + bodyPadding) /
              zoom.zoomRatio -
            zoomPercentageSubtractionLR * zoom.zoomRatio
          }px`,
        top:
          top &&
          `${
            (top - parentTopPosition - calloutCenter + elementCenter) /
            zoom.zoomRatio
          }px`,
      };
      break;
    case 'right':
      callout = {
        right:
          right &&
          `${
            (parentRightPosition - right - calloutWidth + bodyPadding) /
              zoom.zoomRatio -
            zoomPercentageSubtractionLR * zoom.zoomRatio
          }px`,
        top:
          top &&
          `${
            (top - parentTopPosition - calloutCenter + elementCenter) /
            zoom.zoomRatio
          }px`,
      };
      break;
    case 'top':
      callout = {
        left:
          left &&
          `${(left - parentLeftPosition + customWidth) / zoom.zoomRatio}px`,
        top: `${
          (top -
            parentTopPosition -
            calloutHeight +
            bodyPadding +
            -afterContentHeight) /
            zoom.zoomRatio -
          zoomPercentageSubtractionTB * zoom.zoomRatio
        }px`,
      };
      break;
    case 'bottom':
      callout = {
        left:
          left &&
          `${(left - parentLeftPosition + customWidth) / zoom.zoomRatio}px`,
        top: `${
          (top -
            parentTopPosition +
            height -
            bodyPadding -
            -afterContentHeight) /
            zoom.zoomRatio +
          zoomPercentageSubtractionTB * zoom.zoomRatio
        }px`,
      };
      break;

    default:
      break;
  }

  return callout;
};

export const getMarkerPosition = ({
  markerRef,
  position,
  id,
  zoom,
  leftSideView,
}) => {
  const documentId = leftSideView
    ? STATEMENT_CONTENT_PANEL_ZOOM_LEFT_BLOCK
    : STATEMENT_CONTENT_PANEL_ZOOM_BLOCK;
  const parentElement = document && document.getElementById(documentId);
  const {
    left: parentLeftPosition,
    top: parentTopPosition,
    right: parentRightPosition,
  } = parentElement ? parentElement.getBoundingClientRect() : {};

  const tieoutElement = document.getElementById(id);

  const { right, top, left, width, height } = tieoutElement
    ? tieoutElement.getBoundingClientRect()
    : {};

  const annotationMarkerInfo =
    (markerRef &&
      markerRef.current &&
      markerRef.current.getBoundingClientRect()) ||
    {};
  const { width: annotationMarkerElementWidth } = annotationMarkerInfo;

  const elementHorizontalCenter = width / 2;

  const customHeight = (height - annotationMarkerElementWidth) / 2;

  let marker = {};

  switch (position) {
    case 'left':
      marker = {
        left:
          left &&
          `${
            (left - parentLeftPosition - annotationMarkerElementWidth) /
            zoom.zoomRatio
          }px`,
        top:
          top &&
          `${(top - parentTopPosition + customHeight) / zoom.zoomRatio}px`,
      };
      break;
    case 'right':
      marker = {
        right:
          right &&
          `${
            (parentRightPosition - right - annotationMarkerElementWidth) /
            zoom.zoomRatio
          }px`,
        top:
          top &&
          `${(top - parentTopPosition + customHeight) / zoom.zoomRatio}px`,
      };
      break;
    case 'top':
      marker = {
        left:
          left &&
          `${
            (left -
              parentLeftPosition +
              elementHorizontalCenter -
              annotationMarkerElementWidth / 2) /
            zoom.zoomRatio
          }px`,
        top: `${
          (top - parentTopPosition - annotationMarkerElementWidth) /
          zoom.zoomRatio
        }px`,
      };
      break;
    case 'bottom':
      marker = {
        left:
          left &&
          `${
            (left -
              parentLeftPosition +
              elementHorizontalCenter -
              annotationMarkerElementWidth / 2) /
            zoom.zoomRatio
          }px`,
        top: `${
          (top - parentTopPosition + annotationMarkerElementWidth) /
          zoom.zoomRatio
        }px`,
      };
      break;

    default:
      break;
  }

  return marker;
};

export const setCalloutArrowDynamicWidth = ({
  tieoutElement,
  fontSize,
  position,
}) => {
  const scalingFactor = 0.05;
  const dynamicWidthBefore =
    tieoutElement &&
    tieoutElement.offsetHeight * scalingFactor * fontSize + 'px';

  const dynamicWidthAfter =
    tieoutElement &&
    tieoutElement.offsetHeight * scalingFactor * fontSize - 1 + 'px';

  tieoutElement &&
    tieoutElement.style.setProperty(
      `--${position}-arrow-width-before`,
      dynamicWidthBefore,
    );

  tieoutElement &&
    tieoutElement.style.setProperty(
      `--${position}-arrow-width-after`,
      dynamicWidthAfter,
    );

  return { dynamicWidthBefore, dynamicWidthAfter };
};
